/**
 * Custom hook for auto-scroll behavior in chat messages
 *
 * Scroll-per-question strategy (Claude.ai pattern):
 * - When a new message arrives: smooth scroll so the user's question is
 *   at the top of the viewport, giving a clean reading frame.
 * - During streaming: NO auto-scroll. The response grows below the
 *   viewport and the user reads at their own pace.
 * - A "scroll to bottom" button (managed in ChatPanel) lets the user
 *   jump to the latest content when they choose.
 *
 * Why no bottom-tracking during streaming:
 * - Cognitive research shows 10-15% of eye movements are regressions
 *   (re-reading). Auto-scroll blocks this, reducing comprehension.
 * - LLMs generate faster than humans read (~30 tok/s vs ~12 words/s).
 *   Bottom-tracking makes text "scroll away" before it can be read.
 * - NNG Heuristic #3 (User Control): overriding scroll = loss of agency.
 * - Claude.ai, the gold standard, uses this exact pattern.
 */

import { useRef, useEffect, RefObject } from 'react';
import type { ChatMessage } from '../../ChatPanel';

export interface UseAutoScrollProps {
  messages: ChatMessage[];
  messagesContainerRef: RefObject<HTMLDivElement>;
  messagesEndRef: RefObject<HTMLDivElement>;
  lastMessageRef: RefObject<HTMLDivElement>;
}

export function useAutoScroll({
  messages,
  messagesContainerRef,
  messagesEndRef,
  lastMessageRef,
}: UseAutoScrollProps): void {
  const lastMessageCountRef = useRef<number>(0);
  const lastMessageIdRef = useRef<string | null>(null);

  useEffect(() => {
    const currentMessageCount = messages.length;
    const lastMessage = messages.length > 0 ? messages[messages.length - 1] : null;
    const lastMessageId = lastMessage?.id || null;

    const hasNewMessage =
      currentMessageCount > lastMessageCountRef.current ||
      (lastMessageId && lastMessageId !== lastMessageIdRef.current);

    if (hasNewMessage && messagesContainerRef.current) {
      const isInitialLoad = lastMessageCountRef.current === 0;

      if (isInitialLoad) {
        // Page load — scroll to bottom to show latest conversation state
        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' });
      } else if (lastMessageRef.current) {
        // Scroll the user's question to the top of the viewport.
        // Fires both when user sends (question is last element) and when
        // AI response first appears (now there's content below, allowing
        // block:'start' to fully place the question at the top).
        // Must be 'auto' (instant): 'smooth' gets cancelled by rapid DOM changes.
        lastMessageRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start',
        });
      }
      // During streaming: hasNewMessage stays false → no scroll
    }

    // Update refs
    lastMessageCountRef.current = currentMessageCount;
    lastMessageIdRef.current = lastMessageId;
  }, [messages, messagesContainerRef, messagesEndRef, lastMessageRef]);
}

export default useAutoScroll;
